import React from 'react';
import type { ReactElement } from 'react';
import type { Command } from '@commander-js/extra-typings';
import type { CommandServices } from '@wix/cli-command-contract';
import type { UserInfo } from '@wix/cli-auth';
import { SiteAuthProvider } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { addUserFields } from '@wix/cli-telemetry';
import { I18nProvider } from '@wix/cli-i18n';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import { PanoramaProvider } from '@wix/cli-panorama';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import { runCommand } from './run-command.js';
import { ProjectModelProvider } from '../model.js';
import messages from '../messages.json' with { type: 'json' };

export async function renderCommand(
  command: Command,
  services: CommandServices,
  cliVersion: string,
  component: ReactElement
) {
  await runCommand(command, services, cliVersion, async (model) => {
    const { biLogger, errorReporter, panorama } = services;

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    const headers = buildHttpClientHeaders({
      flow: 'Sites',
      command: command.name(),
      cliVersion,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <SiteAuthProvider siteId={model.metaSiteId} onLogin={handleLogin}>
              <HttpClientProvider headers={headers}>
                <I18nProvider messages={messages}>
                  <ProjectModelProvider value={model}>
                    {component}
                  </ProjectModelProvider>
                </I18nProvider>
              </HttpClientProvider>
            </SiteAuthProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}
